import {
  ApiPersonType,
  ApiSanctionEntityType,
  ApiSanctionType,
  ApiScreeningAlertFilterExpressionFieldTypeEnum,
  ApiScreeningAlertFilterExpressionOperationEnum,
  ApiScreeningAlertFilterExpressionTargetEnum,
} from '../../../api';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import {
  InternalApiCustomList,
  InternalApiEntitySchema,
  InternalApiEntitySchemaField,
  InternalApiEntitySchemaFieldType,
  InternalApiScreeningAlertStatus,
  InternalApiUserBasicDetails,
} from '../../../api-internal';
import { isChildField, isComplexField, isVisible } from '../../../schema/helper';
import { systemAlertStatusCodes } from '../../alert-status/alertStatusesConstants';

export class ScreeningAlertFilterObjects {
  constructor(public objects: ScreeningAlertFilterObject[]) {}

  public getObjectOptions(): DropdownOption[] {
    return this.objects.map((it) => it.value);
  }

  public getObject(key: string): ScreeningAlertFilterObject | undefined {
    return this.objects.find((it) => it.value.key === key);
  }

  public createNewDefaultValues(): {
    field: DropdownOption;
    operation: DropdownOption;
    fieldType: ApiScreeningAlertFilterExpressionFieldTypeEnum;
    target: DropdownOption;
  } {
    const object = this.objects[0];
    const field = object.fields[0];
    const operation = field.operations[0];
    return {
      target: object.value,
      field: field.value,
      operation: operation,
      fieldType: field.type,
    };
  }
}

const COMPLEX_DATA_SEPARATOR = '.';

export class ScreeningAlertFilterObject {
  constructor(
    public value: DropdownOption,
    public fields: ScreeningAlertFilterField[]
  ) {}

  public getFieldOptions(): DropdownOption[] {
    return this.fields
      .map((it) => it.value)
      .filter(
        (it) =>
          !it.key.includes(COMPLEX_DATA_SEPARATOR) || [personExternalIdFieldKey, personTypeFieldKey].includes(it.key)
      );
  }

  public getField(key: string): ScreeningAlertFilterField | undefined {
    return this.fields.find((it) => it.value.key === key);
  }
}

export class ScreeningAlertFilterField {
  constructor(
    public value: DropdownOption,
    public type: ApiScreeningAlertFilterExpressionFieldTypeEnum,
    public operations: DropdownOption[],
    public options?: DropdownOption[]
  ) {}

  public getOperation(key: string): DropdownOption {
    const DropdownOption = this.operations.find((it) => it.key === key);
    if (!DropdownOption) {
      throw new Error(`Field ${this.value.key} has no operation ${key}`);
    }
    return DropdownOption;
  }
}

const dateLessThanOperation: DropdownOption = {
  key: ApiScreeningAlertFilterExpressionOperationEnum.LESS_THAN,
  label: 'is before',
  value: ApiScreeningAlertFilterExpressionOperationEnum.LESS_THAN,
};

const dateGreaterThanOperation: DropdownOption = {
  key: ApiScreeningAlertFilterExpressionOperationEnum.GREATER_THAN,
  label: 'is after',
  value: ApiScreeningAlertFilterExpressionOperationEnum.GREATER_THAN,
};

const numericLessThanOperation: DropdownOption = {
  key: ApiScreeningAlertFilterExpressionOperationEnum.LESS_THAN,
  label: 'is less than',
  value: ApiScreeningAlertFilterExpressionOperationEnum.LESS_THAN,
};

const numericGreaterThanOperation: DropdownOption = {
  key: ApiScreeningAlertFilterExpressionOperationEnum.GREATER_THAN,
  label: 'is greater than',
  value: ApiScreeningAlertFilterExpressionOperationEnum.GREATER_THAN,
};

const isBetweenOperation: DropdownOption = {
  key: 'IS_BETWEEN',
  label: 'is between',
  value: 'IS_BETWEEN',
};

const isOneOfOperation: DropdownOption = {
  key: ApiScreeningAlertFilterExpressionOperationEnum.EQUALS_ONE_OF,
  label: 'is',
  value: ApiScreeningAlertFilterExpressionOperationEnum.EQUALS_ONE_OF,
};

const isNotOneOfOperation: DropdownOption = {
  key: ApiScreeningAlertFilterExpressionOperationEnum.NOT_EQUALS_ONE_OF,
  label: 'is not',
  value: ApiScreeningAlertFilterExpressionOperationEnum.NOT_EQUALS_ONE_OF,
};

const equalsOperation: DropdownOption = {
  key: ApiScreeningAlertFilterExpressionOperationEnum.EQUALS,
  label: 'is',
  value: ApiScreeningAlertFilterExpressionOperationEnum.EQUALS,
};

const notEqualsOperation: DropdownOption = {
  key: ApiScreeningAlertFilterExpressionOperationEnum.NOT_EQUALS,
  label: 'is not',
  value: ApiScreeningAlertFilterExpressionOperationEnum.NOT_EQUALS,
};

const containsOperation: DropdownOption = {
  key: ApiScreeningAlertFilterExpressionOperationEnum.PARTLY_MATCHES,
  label: 'contains',
  value: ApiScreeningAlertFilterExpressionOperationEnum.PARTLY_MATCHES,
};

const notContainsOperation: DropdownOption = {
  key: ApiScreeningAlertFilterExpressionOperationEnum.NO_PARTIAL_MATCH,
  label: 'does not contain',
  value: ApiScreeningAlertFilterExpressionOperationEnum.NO_PARTIAL_MATCH,
};

export const timestampOperations = [dateGreaterThanOperation, dateLessThanOperation, isBetweenOperation];

export const numberOperations = [
  equalsOperation,
  numericGreaterThanOperation,
  numericLessThanOperation,
  isBetweenOperation,
];

export const stringListOperations = [isOneOfOperation, isNotOneOfOperation];

export const stringOperations = [equalsOperation, containsOperation, notEqualsOperation, notContainsOperation];
const personExternalIdFieldKey = '$.personExternalID.$';
const personExternalIdField = new ScreeningAlertFilterField(
  {
    key: personExternalIdFieldKey,
    label: 'Person ID',
    value: personExternalIdFieldKey,
  },
  ApiScreeningAlertFilterExpressionFieldTypeEnum.STRING,
  stringOperations
);

const personTypeFieldKey = '$.personType.$';
const personTypeField = new ScreeningAlertFilterField(
  {
    key: personTypeFieldKey,
    label: 'Person Type',
    value: personTypeFieldKey,
  },
  ApiScreeningAlertFilterExpressionFieldTypeEnum.STRING_LIST,
  stringListOperations,
  Object.keys(ApiPersonType).map((type) => ({
    key: type,
    label: type,
    value: type,
  }))
);

const mapToField = (schemaField: InternalApiEntitySchemaField): ScreeningAlertFilterField => {
  const value = {
    key: schemaField.fieldName,
    label: schemaField.displayName || schemaField.fieldName,
    value: schemaField.fieldName,
  };
  switch (schemaField.type) {
    case InternalApiEntitySchemaFieldType.KEYWORD:
    case InternalApiEntitySchemaFieldType.TEXT:
    case InternalApiEntitySchemaFieldType.ENUM:
      return new ScreeningAlertFilterField(
        value,
        ApiScreeningAlertFilterExpressionFieldTypeEnum.STRING,
        stringOperations
      );
    case InternalApiEntitySchemaFieldType.DATETIME:
      return new ScreeningAlertFilterField(
        value,
        ApiScreeningAlertFilterExpressionFieldTypeEnum.TIMESTAMP,
        timestampOperations
      );
    case InternalApiEntitySchemaFieldType.NUMBER:
      return new ScreeningAlertFilterField(
        value,
        ApiScreeningAlertFilterExpressionFieldTypeEnum.NUMBER,
        numberOperations
      );
    default:
      throw new Error(
        `No corresponding ApiScreeningAlertFilterExpressionFieldTypeEnum for schema type ${schemaField.type}`
      );
  }
};

export const getFilterObjects = (
  users: Array<InternalApiUserBasicDetails> | undefined,
  statuses: Array<InternalApiScreeningAlertStatus>,
  personSchema: InternalApiEntitySchema | undefined,
  transactionSchema: InternalApiEntitySchema | undefined,
  screeningLists: InternalApiCustomList[] | null,
  adverseMediaEntitiesEnabled: boolean
): ScreeningAlertFilterObjects => {
  const usersOptions = users?.map(
    (user) =>
      ({
        key: `${user.id}`,
        label: user.name,
        value: `${user.id}`,
      }) as DropdownOption
  );
  const dowJonesOptions: DropdownOption[] = [
    {
      key: ApiSanctionType.SANCTION,
      label: 'Sanction',
      value: ApiSanctionType.SANCTION,
    },
    {
      key: ApiSanctionType.PEP,
      label: 'PEP/RCA',
      value: ApiSanctionType.PEP,
    },
    {
      key: ApiSanctionType.ADVERSE_MEDIA,
      label: 'Adverse Media',
      value: ApiSanctionType.ADVERSE_MEDIA,
    },
  ];
  if (adverseMediaEntitiesEnabled) {
    dowJonesOptions.push({
      key: ApiSanctionType.ADVERSE_MEDIA_ENTITY,
      label: 'Adverse Media Entity',
      value: ApiSanctionType.ADVERSE_MEDIA_ENTITY,
    });
  }
  const screeningListOptions =
    screeningLists?.map(
      (list) =>
        ({
          key: list.id,
          label: list.name,
          value: list.id,
        }) as DropdownOption
    ) || [];
  const listTypeOptions = dowJonesOptions.concat(screeningListOptions);
  const personFields = [personExternalIdField, personTypeField].concat(
    personSchema?.schemaFields
      .filter((it) => !isComplexField(it) && !isChildField(it) && isVisible(it))
      .map((it) => mapToField(it)) || []
  );
  const transactionFields =
    transactionSchema?.schemaFields
      .filter((it) => !isComplexField(it) && !isChildField(it) && isVisible(it))
      .map((it) => mapToField(it)) || [];

  const statusOptions = statuses
    .filter(
      (status) => status.enabledScreening && statuses.find((status2) => status2.code === status.code)?.id === status.id
    )
    .filter((status) => status.code !== systemAlertStatusCodes.filtered)
    .map((status) => toDropdownOption(status.code));

  const entityTypeOptions: DropdownOption[] = [
    {
      key: ApiSanctionEntityType.PERSON,
      label: 'Person',
      value: ApiSanctionEntityType.PERSON,
    },
    {
      key: ApiSanctionEntityType.TRANSACTION,
      label: 'Transaction',
      value: ApiSanctionEntityType.TRANSACTION,
    },
  ];

  const alertFields = [
    new ScreeningAlertFilterField(
      { key: 'id', label: 'ID', value: 'id' },
      ApiScreeningAlertFilterExpressionFieldTypeEnum.NUMBER,
      numberOperations
    ),
    new ScreeningAlertFilterField(
      { key: 'entityType', label: 'Screened entity', value: 'listType' },
      ApiScreeningAlertFilterExpressionFieldTypeEnum.STRING_LIST,
      stringListOperations,
      entityTypeOptions
    ),
    new ScreeningAlertFilterField(
      { key: 'listType', label: 'Type', value: 'listType' },
      ApiScreeningAlertFilterExpressionFieldTypeEnum.STRING_LIST,
      stringListOperations,
      listTypeOptions
    ),
    new ScreeningAlertFilterField(
      { key: 'currentStatus', label: 'Status', value: 'currentStatus' },
      ApiScreeningAlertFilterExpressionFieldTypeEnum.STRING_LIST,
      stringListOperations,
      statusOptions
    ),
    new ScreeningAlertFilterField(
      { key: 'assignedTo', label: 'Assigned to', value: 'assignedTo' },
      ApiScreeningAlertFilterExpressionFieldTypeEnum.STRING_LIST,
      stringListOperations,
      usersOptions
    ),
    new ScreeningAlertFilterField(
      { key: 'createdTime', label: 'Created at', value: 'createdTime' },
      ApiScreeningAlertFilterExpressionFieldTypeEnum.TIMESTAMP,
      timestampOperations
    ),
    new ScreeningAlertFilterField(
      {
        key: 'lastStatusChangeTime',
        label: 'Status set at',
        value: 'lastStatusChangeTime',
      },
      ApiScreeningAlertFilterExpressionFieldTypeEnum.TIMESTAMP,
      timestampOperations
    ),
    new ScreeningAlertFilterField(
      {
        key: 'lastStatusChangeBy',
        label: 'Status set by',
        value: 'lastStatusChangeBy',
      },
      ApiScreeningAlertFilterExpressionFieldTypeEnum.STRING_LIST,
      stringListOperations,
      usersOptions
    ),
  ];

  const screeningAlertFilterObjects: ScreeningAlertFilterObject[] = [
    new ScreeningAlertFilterObject(
      {
        key: ApiScreeningAlertFilterExpressionTargetEnum.ALERT,
        label: 'Screening alert',
        value: ApiScreeningAlertFilterExpressionTargetEnum.ALERT,
      },
      alertFields
    ),
  ];

  if (personSchema && personSchema.schemaFields.length > 0) {
    screeningAlertFilterObjects.push(
      new ScreeningAlertFilterObject(
        {
          key: ApiScreeningAlertFilterExpressionTargetEnum.PERSON,
          label: 'Person',
          value: ApiScreeningAlertFilterExpressionTargetEnum.PERSON,
        },
        personFields || []
      )
    );
  }

  if (transactionSchema && transactionSchema.schemaFields.length > 0) {
    screeningAlertFilterObjects.push(
      new ScreeningAlertFilterObject(
        {
          key: ApiScreeningAlertFilterExpressionTargetEnum.TRANSACTION,
          label: 'Transaction',
          value: ApiScreeningAlertFilterExpressionTargetEnum.TRANSACTION,
        },
        transactionFields || []
      )
    );
  }

  return new ScreeningAlertFilterObjects(screeningAlertFilterObjects);
};

export const toDropdownOption = (value: string): DropdownOption => {
  return {
    key: value,
    label: value,
    value: value,
  };
};
